import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Save, Server, Link, Shield, RefreshCw, Code, BookOpen, Database, ArrowRightLeft, CheckCircle2, Copy, Terminal, DownloadCloud, Image as ImageIcon } from "lucide-react";
import { REACT_APP_API_URL } from "config/api-config";

export default function HRMSSetup({ onClose }) {
  const [activeTab, setActiveTab] = useState("outbound"); 
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [copiedStates, setCopiedStates] = useState({});
  
  const apiUrl = `${REACT_APP_API_URL}/events/hrms/sync_staff`;

  const [formData, setFormData] = useState({
    webhook_url: "",
    api_key: "",
    auto_sync: true,
    inbound_api_key: "Loading...",
    allowed_ips: "",
    pull_url: "",
    pull_token: ""
  });

  const [savedData, setSavedData] = useState({
    webhook_url: "",
    api_key: "",
    auto_sync: true,
    allowed_ips: "",
    pull_url: "",
    pull_token: ""
  });

  useEffect(() => {
    fetchHRMSConfig();
  }, []);

  const fetchHRMSConfig = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(`${REACT_APP_API_URL}/events/hrms/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        const fetchedConfig = {
          webhook_url: response.data.webhook_url || "",
          api_key: response.data.api_key || "",
          auto_sync: response.data.auto_sync !== false,
          inbound_api_key: response.data.inbound_api_key || "",
          allowed_ips: response.data.allowed_ips || "",
          pull_url: response.data.pull_url || "",
          pull_token: response.data.pull_token || ""
        };
        setFormData(fetchedConfig);
        setSavedData({ ...fetchedConfig, inbound_api_key: undefined }); 
      }
    } catch (error) {
      toast.error("Failed to fetch HRMS config");
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${REACT_APP_API_URL}/events/hrms/config`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("HRMS Configuration Saved!");
      setSavedData({ ...formData, inbound_api_key: undefined });
    } catch (error) {
      toast.error("Failed to save configuration.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetKey = async () => {
    if(!window.confirm("Are you sure? This breaks current HRMS integrations pushing data to CamSec Ai.")) return;
    setResetting(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${REACT_APP_API_URL}/events/hrms/reset_key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({ ...formData, inbound_api_key: res.data.inbound_api_key });
      toast.success("New inbound key generated.");
    } catch (error) {
      toast.error("Failed to reset API key.");
    } finally {
      setResetting(false);
    }
  };

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.post(`${REACT_APP_API_URL}/events/hrms/manual_sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Successfully synced ${response.data.synced_count} records to HRMS!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Manual sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const handleFetchStaff = async () => {
    setFetching(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${REACT_APP_API_URL}/events/hrms/fetch_database`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.detail);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync HRMS database.");
    } finally {
      setFetching(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedStates({ ...copiedStates, [key]: true });
    setTimeout(() => setCopiedStates({ ...copiedStates, [key]: false }), 2000);
  };

  const hasUnsavedChanges = 
    formData.webhook_url !== savedData.webhook_url ||
    formData.api_key !== savedData.api_key ||
    formData.auto_sync !== savedData.auto_sync ||
    formData.allowed_ips !== savedData.allowed_ips ||
    formData.pull_url !== savedData.pull_url ||
    formData.pull_token !== savedData.pull_token;
    
  const isUrlEmpty = formData.webhook_url.trim() === "";
  const isSyncDisabled = syncing || hasUnsavedChanges || isUrlEmpty;

  const jsonPayload = `{"staff_id": "EMP-1002", "first_name": "Sarah", "last_name": "Connor", "department": "Engineering"}`;
  const curlCommand = `curl -X POST ${apiUrl} \\\n-H "Content-Type: application/json" \\\n-H "x-api-key: ${formData.inbound_api_key}" \\\n-d '${jsonPayload}'`;

  return (
    <div style={{ backgroundColor: "#FFFFFF", borderRadius: "24px", overflow: "hidden", boxShadow: "0px 10px 40px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", maxHeight: "85vh" }}>
      
      {/* HEADER */}
      <div style={{ backgroundColor: "#F4F7FE", padding: "24px 30px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E2E8F0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ width: "48px", height: "48px", borderRadius: "12px", backgroundColor: "#4318FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Server size={24} color="white" />
          </div>
          <div>
            <h3 style={{ margin: 0, color: "#2B3674", fontSize: "20px", fontWeight: "700" }}>HRMS Integration</h3>
            <p style={{ margin: 0, color: "#A3AED0", fontSize: "14px" }}>Enterprise Staff Sync & Attendance Logic</p>
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#A3AED0" }}><X size={24} /></button>
      </div>

      {/* TABS */}
      <div style={{ display: "flex", borderBottom: "1px solid #E2E8F0", backgroundColor: "#FAFCFE", padding: "0 30px", gap: "10px" }}>
        <button onClick={() => setActiveTab("outbound")} style={{ padding: "16px 12px", background: "none", border: "none", borderBottom: activeTab === "outbound" ? "3px solid #4318FF" : "3px solid transparent", color: activeTab === "outbound" ? "#4318FF" : "#A3AED0", fontWeight: "700", fontSize: "14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
          <ArrowRightLeft size={16} /> Send Attendance Logs
        </button>
        <button onClick={() => setActiveTab("inbound")} style={{ padding: "16px 12px", background: "none", border: "none", borderBottom: activeTab === "inbound" ? "3px solid #4318FF" : "3px solid transparent", color: activeTab === "inbound" ? "#4318FF" : "#A3AED0", fontWeight: "700", fontSize: "14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
          <Database size={16} /> Sync Employee Data
        </button>
        <button onClick={() => setActiveTab("docs")} style={{ padding: "16px 12px", background: "none", border: "none", borderBottom: activeTab === "docs" ? "3px solid #4318FF" : "3px solid transparent", color: activeTab === "docs" ? "#4318FF" : "#A3AED0", fontWeight: "700", fontSize: "14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
          <Code size={16} /> API Docs
        </button>
      </div>

      {/* SCROLLABLE BODY */}
      <div style={{ padding: "30px", overflowY: "auto", flex: 1 }}>
        
        {/* OUTBOUND */}
        {activeTab === "outbound" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px", animation: "fadeIn 0.3s ease-out" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
                <Link size={16} color="#4318FF"/> Destination Webhook URL
              </label>
              <input type="text" placeholder="https://your-company.com/api/hrms/attendance" value={formData.webhook_url} onChange={(e) => setFormData({...formData, webhook_url: e.target.value})} style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", backgroundColor: "#F8FAFC" }} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
                <Shield size={16} color="#4318FF"/> Authentication Token (Optional)
              </label>
              <input type="password" placeholder="Token required by your HRMS" value={formData.api_key} onChange={(e) => setFormData({...formData, api_key: e.target.value})} style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", backgroundColor: "#F8FAFC" }} />
            </div>

            <div style={{ backgroundColor: "#FFFFFF", padding: "16px", borderRadius: "12px", border: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h4 style={{ margin: "0 0 4px 0", color: "#2B3674", fontSize: "15px", fontWeight: "700" }}>Real-Time Auto Sync</h4>
                <p style={{ margin: 0, color: "#A3AED0", fontSize: "13px" }}>Stream live attendance records to HRMS instantly.</p>
              </div>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer", position: "relative" }}>
                <input type="checkbox" style={{ opacity: 0, position: "absolute", width: 0, height: 0 }} checked={formData.auto_sync} onChange={(e) => setFormData({...formData, auto_sync: e.target.checked})} />
                <div style={{ width: "48px", height: "26px", backgroundColor: formData.auto_sync ? "#05CD99" : "#E2E8F0", borderRadius: "20px", position: "relative", transition: "0.3s", display: "flex", alignItems: "center" }}>
                  <div style={{ width: "20px", height: "20px", backgroundColor: "white", borderRadius: "50%", position: "absolute", left: formData.auto_sync ? "25px" : "3px", transition: "0.3s" }}></div>
                </div>
              </label>
            </div>

            <div style={{ display: "flex", gap: "15px", marginTop: "10px" }}>
              <button onClick={handleManualSync} disabled={isSyncDisabled} style={{ flex: 1, padding: "14px", backgroundColor: isSyncDisabled ? "#F4F7FE" : "#fff8f1", color: isSyncDisabled ? "#A3AED0" : "#FF912C", border: isSyncDisabled ? "1px solid #E2E8F0" : "1px solid #ffe4cc", borderRadius: "12px", fontWeight: "700", cursor: isSyncDisabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                <RefreshCw size={18} className={syncing ? "animate-spin" : ""} /> {syncing ? "Pushing Data..." : "Manual Sync Today"}
              </button>
              <button onClick={handleSave} disabled={loading || (!hasUnsavedChanges && !isUrlEmpty)} style={{ flex: 1, padding: "14px", backgroundColor: (!hasUnsavedChanges && !isUrlEmpty) ? "#A3AED0" : "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: (loading || (!hasUnsavedChanges && !isUrlEmpty)) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                <Save size={18} /> {loading ? "Saving..." : "Save Config"}
              </button>
            </div>
          </div>
        )}

        {/* INBOUND */}
        {activeTab === "inbound" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px", animation: "fadeIn 0.3s ease-out" }}>
            
            <div style={{ border: "1px solid #E2E8F0", padding: "20px", borderRadius: "12px" }}>
              <h4 style={{ margin: "0 0 15px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px" }}><Terminal size={18} color="#4318FF"/> Option A: Live API Push (From HRMS)</h4>
              <p style={{ margin: "0 0 20px 0", color: "#475467", fontSize: "13px", lineHeight: "1.6" }}>Provide this API endpoint to your IT team. When a new employee is onboarded in the HRMS, their profile is instantly pushed into CamSec Ai.</p>
              
              <div style={{ marginBottom: "15px" }}>
                <p style={{ margin: "0 0 6px 0", fontSize: "12px", color: "#2B3674", fontWeight: "700" }}>YOUR SECRET INBOUND KEY</p>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input type="text" readOnly value={formData.inbound_api_key} style={{ flexGrow: 1, padding: "12px", borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "14px", backgroundColor: "#F8FAFC", color: "#EE5D50", fontWeight: "600" }} />
                  <button onClick={() => copyToClipboard(formData.inbound_api_key, "inboundKey")} style={{ padding: "10px 16px", backgroundColor: "#F4F7FE", color: "#4318FF", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer" }}>
                    {copiedStates["inboundKey"] ? "Copied" : "Copy"}
                  </button>
                  <button onClick={handleResetKey} disabled={resetting} style={{ padding: "10px 16px", backgroundColor: "#ffeeec", color: "#EE5D50", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer" }}>Reset</button>
                </div>
              </div>
              
              <div style={{ marginBottom: "15px" }}>
                <p style={{ margin: "0 0 6px 0", fontSize: "12px", color: "#2B3674", fontWeight: "700" }}>WHITELISTED IPs (Optional)</p>
                <input type="text" placeholder="e.g. 203.0.113.45" value={formData.allowed_ips} onChange={(e) => setFormData({...formData, allowed_ips: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #E2E8F0" }} />
              </div>

              <button onClick={handleSave} disabled={loading} style={{ padding: "12px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}><Save size={16}/> Save Security Settings</button>
            </div>

            <div style={{ border: "1px solid #E2E8F0", padding: "20px", borderRadius: "12px", backgroundColor: "#FAFCFE" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "15px", marginBottom: "15px" }}>
                <div style={{ width: "42px", height: "42px", borderRadius: "10px", backgroundColor: "#FFF4EB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DownloadCloud size={22} color="#FF912C"/>
                </div>
                <div>
                  <h4 style={{ margin: "0 0 6px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>Option B: Full Database Migration (Fetch API)</h4>
                  <p style={{ margin: "0 0 8px 0", color: "#475467", fontSize: "13px", lineHeight: "1.6" }}>
                    CamSec Ai can download the entire employee list at once via your HRMS GET API.
                  </p>
                  <p style={{ margin: 0, color: "#05CD99", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                    <ImageIcon size={14} /> Supports Automatic AI Face Processing via Base64 Photos.
                  </p>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: "#2B3674" }}>HRMS GET API URL</label>
                  <input type="text" placeholder="https://your-hrms.com/api/get-employees" value={formData.pull_url} onChange={(e) => setFormData({...formData, pull_url: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #E2E8F0", marginTop: "4px" }} />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "700", color: "#2B3674" }}>AUTHENTICATION TOKEN</label>
                  <input type="password" placeholder="Bearer Token (Optional)" value={formData.pull_token} onChange={(e) => setFormData({...formData, pull_token: e.target.value})} style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #E2E8F0", marginTop: "4px" }} />
                </div>
              </div>

              <div style={{ display: "flex", gap: "15px" }}>
                <button onClick={handleFetchStaff} disabled={fetching || !formData.pull_url} style={{ flex: 1, padding: "12px", backgroundColor: fetching || !formData.pull_url ? "#E2E8F0" : "#FF912C", color: fetching || !formData.pull_url ? "#A3AED0" : "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: fetching || !formData.pull_url ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <RefreshCw size={16} className={fetching ? "animate-spin" : ""} /> {fetching ? "Syncing Database..." : "Sync Database Now"}
                </button>
                <button onClick={handleSave} disabled={loading} style={{ flex: 1, padding: "12px", backgroundColor: "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <Save size={16}/> Save Configuration
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DOCS */}
        {activeTab === "docs" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px", animation: "fadeIn 0.3s ease-out" }}>
            <div style={{ backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", border: "1px solid #E2E8F0" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", display: "flex", alignItems: "center", gap: "8px", fontWeight: "700" }}>
                <BookOpen size={18} color="#4318FF" /> Attendance Webhook Payload (Outbound)
              </h4>
              <p style={{ margin: 0, color: "#475467", fontSize: "13px", lineHeight: "1.6" }}>Sent to your HRMS when an employee's face is detected.</p>
              <pre style={{ margin: "10px 0 0 0", padding: "15px", backgroundColor: "#0B1437", color: "#00E396", borderRadius: "8px", fontSize: "13px" }}>
{`{
  "staff_id": "EMP-1002",
  "staff_name": "Sarah Connor",
  "department": "Engineering",
  "event_type": "CHECK_IN",
  "timestamp": "2026-08-01 08:55:12"
}`}
              </pre>
            </div>
            
            <div style={{ backgroundColor: "#F0EBFF", padding: "20px", borderRadius: "12px", border: "1px solid #D4C4FF" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", display: "flex", alignItems: "center", gap: "8px", fontWeight: "700" }}>
                <ImageIcon size={18} color="#4318FF" /> Database Migration JSON Format (Inbound Fetch)
              </h4>
              <p style={{ margin: "0 0 15px 0", color: "#475467", fontSize: "13px", lineHeight: "1.6" }}>
                Return a JSON array of employees. Include a Base64 string in the <code>image</code> key for automatic facial recognition profile creation.
              </p>
              <pre style={{ margin: 0, padding: "15px", backgroundColor: "#0B1437", color: "#00E396", borderRadius: "8px", fontSize: "13px", overflowX: "auto" }}>
{`{
  "data": [
    {
      "staff_id": "EMP-901",
      "first_name": "John",
      "last_name": "Connor",
      "department": "Security",
      "mobile_no": "789654147",
      "image": "/9j/4AAQ..." 
    }
  ]
}`}
              </pre>
            </div>

            <div style={{ backgroundColor: "#0B1437", padding: "16px", borderRadius: "12px", border: "1px solid #2B3674" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#A3AED0", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
                  <Terminal size={14} /> cURL PUSH EXAMPLE (Option A)
                </p>
                <button onClick={() => copyToClipboard(curlCommand, "curl")} style={{ background: "none", border: "none", color: "#00E396", cursor: "pointer", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedStates["curl"] ? <CheckCircle2 size={14} /> : <Copy size={14} />} COPY
                </button>
              </div>
              <pre style={{ margin: 0, padding: "12px", backgroundColor: "#111C44", color: "#00E396", borderRadius: "8px", fontSize: "13px", fontFamily: "monospace", overflowX: "auto" }}>
                {curlCommand}
              </pre>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
